// external
import React, { FC, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ArticleIcon from '@mui/icons-material/Article';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// internal
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  getFocusOptionsFromMapping,
  getSportMappings,
  getSportsOptionsFromMapping,
  getSportsTypeOptionsFromMapping,
} from '../../helpers/app-context.helpers';
import COEDurationInput from '../general/coe-duration-input/coe-duration-input.component';
import COETrainingDurationInput from '../coe-training-duration-input/coe-training-duration-input.component';
import { WORKOUT_LOG_ZONE_DISPLAY_CONFIG } from '../coe-training-modal-v2/components/workout-zone.constants';
import { formatDuration, getMeasurementUnit } from '../../helpers/general.helpers';
import { FLOAT_VALIDATION_REGEX, INTEGER_VALIDATION_REGEX } from '../../constants/regex.constants';
import useService from '../../hooks/service.hook';
import TrainingsService from '../../services/trainings.service';
import { useSnackbar } from 'notistack';
import { palette } from '../../theme';
import {
  FieldErrors,
  FieldErrorCaption,
  errorRowSx,
  iconBox,
  useFieldErrors,
  scrollToFirstError,
} from '../form-validation';

interface COECreatePersonalTrainingModalProps {
  open: boolean;
  onClose: () => void;
  handleRefresh: () => void;
  mode?: 'log' | 'plan';
}

type TrainingFormField = 'title' | 'time' | 'location' | 'description' | 'sport' | 'sportType' | 'focus' | 'distance';

const EMPTY_TRAINING_DATA = {
  title: '',
  time: null as any,
  location: '',
  description: '',
  sport: '',
  sportType: '',
  focus: '',
  complete: false,
  duration: 0,
  distance: 0,
  durationComplete: [0, 0, 0, 0, 0, 0],
  distanceComplete: [0, 0, 0, 0, 0, 0],
};

const COECreatePersonalTrainingModal: FC<COECreatePersonalTrainingModalProps> = (props) => {
  const isLog = props.mode === 'log';
  const sportsMappings = getSportMappings();
  const [trainingForm, setTrainingForm] = useState<any>({ ...EMPTY_TRAINING_DATA, complete: isLog });
  const [loading, setLoading] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');
  const { fieldErrors, setFieldErrors, clearFieldError, resetFieldErrors } = useFieldErrors<TrainingFormField>();
  const [activeTab, setActiveTab] = useState(0);

  const trainingsService = useService(TrainingsService);
  const { enqueueSnackbar } = useSnackbar();
  const measurementUnit = getMeasurementUnit();
  const totalDuration = trainingForm.durationComplete
    .slice(1)
    .reduce((acc: number, val: number) => acc + (val || 0), 0);
  const totalDistance = trainingForm.distanceComplete
    .slice(1)
    .reduce((acc: number, val: number) => acc + (val || 0), 0);

  const handleDurationChange = (seconds: number, idx: number) => {
    setTrainingForm((v: any) => {
      const updated = [...v.durationComplete];
      updated[idx] = seconds;
      return { ...v, durationComplete: updated };
    });
  };

  const handleDistanceChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    setTrainingForm((v: any) => {
      const updated = [...v.distanceComplete];
      updated[idx] = parseFloat(e.target.value) || 0;
      return { ...v, distanceComplete: updated };
    });
  };

  useEffect(() => {
    if (props.open) {
      setLoading(false);
      setSubmitError('');
      resetFieldErrors();
      setActiveTab(0);
      const defaultTime = isLog
        ? new Date()
        : (() => {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(7, 0, 0, 0);
            return tomorrow;
          })();
      setTrainingForm({ ...EMPTY_TRAINING_DATA, complete: isLog, time: defaultTime });
    }
  }, [props.open, isLog]);

  const createTraining = () => {
    if (validate()) {
      setLoading(true);
      const durationComplete = [0, ...trainingForm.durationComplete.slice(1)];
      const distanceComplete = [0, ...trainingForm.distanceComplete.slice(1)].map((v: any) => parseFloat(v) || 0);
      const payload = {
        ...trainingForm,
        durationComplete,
        distanceComplete,
      };
      trainingsService
        .createPersonalTraining(payload)
        .then(() => {
          enqueueSnackbar('Training successfully created', {
            autoHideDuration: 3000,
            variant: 'success',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
          props.handleRefresh();
          props.onClose();
        })
        .catch(() => {
          setLoading(false);
          setSubmitError('Error while creating training. Please try again later');
        });
    }
  };

  // Collects errors for every field in one pass (instead of short-circuiting on the
  // first bad field) so the user sees everything that needs fixing at once.
  const validate = () => {
    const errors: FieldErrors<TrainingFormField> = {};

    if (trainingForm.title.length < 1) {
      errors.title = 'Title is required';
    } else if (trainingForm.title.length > 30) {
      errors.title = 'Max character count for title exceeded';
    }

    if (trainingForm.time === null) {
      errors.time = 'Time is required';
    } else if (trainingForm.time.toString() === 'Invalid Date') {
      errors.time = 'Time is invalid';
    }

    if (trainingForm.location.length < 1) {
      errors.location = 'Location is required';
    } else if (trainingForm.location.length > 36) {
      errors.location = 'Max character count for location exceeded';
    }

    if (trainingForm.description.length < 1) {
      errors.description = 'Description is required';
    } else if (trainingForm.description.length > 1500) {
      errors.description = 'Max character count for description exceeded';
    }

    if (trainingForm.sport.length < 1) {
      errors.sport = 'Sport is required';
    }

    if (trainingForm.sportType.length < 1) {
      errors.sportType = 'Sport type is required';
    }

    if (trainingForm.focus.length < 1) {
      errors.focus = 'Focus is required';
    }

    if (!FLOAT_VALIDATION_REGEX.test(trainingForm.distance) && !INTEGER_VALIDATION_REGEX.test(trainingForm.distance)) {
      errors.distance = 'Distance planned contains invalid characters';
    }

    setFieldErrors(errors);
    scrollToFirstError('training-field', errors);

    return Object.keys(errors).length === 0;
  };

  const handleTimeChange = (newTime: Date | null) => {
    setTrainingForm((v: any) => {
      const base = v.time ? new Date(v.time) : new Date();
      if (newTime) base.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
      return { ...v, time: base };
    });
  };

  const handleDateChange = (newDate: Date | null) => {
    setTrainingForm((v: any) => {
      const base = v.time ? new Date(v.time) : new Date();
      if (newDate) base.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
      return { ...v, time: base };
    });
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, width: 600, maxWidth: 600, maxHeight: '90vh' } }}
    >
      {/* ── Header ── */}
      <Box sx={{ position: 'relative', padding: '20px 24px 16px' }}>
        <Typography variant="h5" fontWeight={700}>
          {isLog ? 'Log Training' : 'Plan Training'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {isLog ? 'Record a completed workout' : 'Schedule a future session'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            &nbsp;·&nbsp;
          </Typography>
          <Chip
            label="Personal"
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: 11, height: 20, borderRadius: 1 }}
          />
          {!isLog && (
            <Typography variant="body2" color="text.secondary">
              &nbsp;·&nbsp;Draft
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={props.onClose}
          size="small"
          aria-label="Close"
          sx={{ position: 'absolute', top: 16, right: 16 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Scrollable content ── */}
      <DialogContent sx={{ padding: '0 16px 8px', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 0 }}>
              {submitError}
            </Alert>
          )}
          {!submitError && Object.keys(fieldErrors).length > 0 && (
            <Alert severity="error" sx={{ mb: 0 }}>
              Please fix the highlighted fields below
            </Alert>
          )}

          {/* Card 1: Title + Description */}
          <Box sx={{ bgcolor: palette.grey[100], borderRadius: 2, overflow: 'hidden' }}>
            <Box
              id="training-field-title"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                padding: '12px 16px',
                ...(fieldErrors.title && errorRowSx),
              }}
            >
              {iconBox(
                palette.purple.light,
                palette.purple.main,
                <TextFieldsIcon fontSize="small" />,
                !!fieldErrors.title
              )}
              <InputBase
                placeholder="Title"
                value={trainingForm.title}
                onChange={(e) => {
                  setTrainingForm((v: any) => ({ ...v, title: e.target.value }));
                  clearFieldError('title');
                }}
                fullWidth
                sx={{ fontSize: 15 }}
              />
            </Box>
            <FieldErrorCaption message={fieldErrors.title} />
            <Divider />
            <Box
              id="training-field-description"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                padding: '12px 16px',
                ...(fieldErrors.description && errorRowSx),
              }}
            >
              {iconBox(
                palette.primary.light,
                palette.primary.main,
                <FormatListBulletedIcon fontSize="small" />,
                !!fieldErrors.description
              )}
              <InputBase
                placeholder="Description"
                value={trainingForm.description}
                onChange={(e) => {
                  setTrainingForm((v: any) => ({ ...v, description: e.target.value }));
                  clearFieldError('description');
                }}
                fullWidth
                multiline
                minRows={2}
                sx={{ fontSize: 15 }}
              />
            </Box>
            <FieldErrorCaption message={fieldErrors.description} />
          </Box>

          {/* Card 2: Time + Date + Location */}
          <Box sx={{ bgcolor: palette.grey[100], borderRadius: 2, overflow: 'hidden' }}>
            {/* Time */}
            <Box
              id="training-field-time"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                padding: '10px 16px',
                ...(fieldErrors.time && errorRowSx),
              }}
            >
              {iconBox('#FFF3E0', '#F57C00', <AccessTimeIcon fontSize="small" />, !!fieldErrors.time)}
              <Typography sx={{ flex: 1, fontWeight: 500 }}>Time</Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  value={trainingForm.time}
                  onChange={(newTime) => {
                    handleTimeChange(newTime);
                    clearFieldError('time');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          textAlign: 'right',
                          color: palette.primary.main,
                          fontWeight: 600,
                          fontSize: 15,
                          padding: 0,
                        },
                      }}
                      sx={{ width: 100 }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
            <FieldErrorCaption message={fieldErrors.time} />
            <Divider />
            {/* Date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, padding: '10px 16px' }}>
              {iconBox(palette.primary.light, palette.primary.main, <CalendarTodayIcon fontSize="small" />)}
              <Typography sx={{ flex: 1, fontWeight: 500 }}>Date</Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={trainingForm.time}
                  onChange={handleDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          textAlign: 'right',
                          color: palette.text.secondary,
                          fontSize: 15,
                          padding: 0,
                        },
                      }}
                      sx={{ width: 130 }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
            <Divider />
            {/* Location */}
            <Box
              id="training-field-location"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                padding: '10px 16px',
                ...(fieldErrors.location && errorRowSx),
              }}
            >
              {iconBox('#E8F5E9', '#2E7D32', <LocationOnIcon fontSize="small" />, !!fieldErrors.location)}
              <Typography sx={{ flex: 1, fontWeight: 500 }}>Location</Typography>
              <InputBase
                placeholder="Add location"
                value={trainingForm.location}
                onChange={(e) => {
                  setTrainingForm((v: any) => ({ ...v, location: e.target.value }));
                  clearFieldError('location');
                }}
                sx={{ fontSize: 15, color: palette.text.secondary, '& input': { textAlign: 'right' } }}
              />
            </Box>
            <FieldErrorCaption message={fieldErrors.location} />
          </Box>

          {/* Tab strip */}
          <Box sx={{ bgcolor: palette.grey[100], borderRadius: 2, padding: '4px' }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                minHeight: 38,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': {
                  minHeight: 38,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  flex: 1,
                  color: palette.text.secondary,
                },
                '& .Mui-selected': {
                  bgcolor: 'white',
                  color: `${palette.primary.main} !important`,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                },
              }}
            >
              <Tab icon={<ArticleIcon fontSize="small" />} iconPosition="start" label="Training Details" />
              <Tab icon={<TaskAltIcon fontSize="small" />} iconPosition="start" label="Forms" disabled />
            </Tabs>
          </Box>

          {/* Training Details tab content */}
          {activeTab === 0 && (
            <Box>
              {/* Sport / Sport Type / Focus */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                <FormControl fullWidth size="small" error={!!fieldErrors.sport} id="training-field-sport">
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={fieldErrors.sport ? 'error' : 'text.secondary'}
                    sx={{ letterSpacing: 0.6, mb: 0.5 }}
                  >
                    SPORT
                  </Typography>
                  <Select
                    value={trainingForm.sport}
                    onChange={(e) => {
                      setTrainingForm((v: any) => ({ ...v, sport: e.target.value, sportType: '', focus: '' }));
                      clearFieldError('sport');
                    }}
                    displayEmpty
                    renderValue={(v) => (v ? v : <span style={{ color: palette.text.disabled }}>Select</span>)}
                  >
                    {getSportsOptionsFromMapping(sportsMappings).map((s: string) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.sport && <FormHelperText>{fieldErrors.sport}</FormHelperText>}
                </FormControl>

                <FormControl
                  fullWidth
                  size="small"
                  disabled={!trainingForm.sport}
                  error={!!fieldErrors.sportType}
                  id="training-field-sportType"
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={fieldErrors.sportType ? 'error' : 'text.secondary'}
                    sx={{ letterSpacing: 0.6, mb: 0.5 }}
                  >
                    SPORT TYPE
                  </Typography>
                  <Select
                    value={trainingForm.sportType}
                    onChange={(e) => {
                      setTrainingForm((v: any) => ({ ...v, sportType: e.target.value }));
                      clearFieldError('sportType');
                    }}
                    displayEmpty
                    renderValue={(v) => (v ? v : <span style={{ color: palette.text.disabled }}>Select</span>)}
                  >
                    {(getSportsTypeOptionsFromMapping(sportsMappings, trainingForm.sport) || []).map((s: string) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.sportType && <FormHelperText>{fieldErrors.sportType}</FormHelperText>}
                </FormControl>

                <FormControl
                  fullWidth
                  size="small"
                  disabled={!trainingForm.sport}
                  error={!!fieldErrors.focus}
                  id="training-field-focus"
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={fieldErrors.focus ? 'error' : 'text.secondary'}
                    sx={{ letterSpacing: 0.6, mb: 0.5 }}
                  >
                    FOCUS
                  </Typography>
                  <Select
                    value={trainingForm.focus}
                    onChange={(e) => {
                      setTrainingForm((v: any) => ({ ...v, focus: e.target.value }));
                      clearFieldError('focus');
                    }}
                    displayEmpty
                    renderValue={(v) => (v ? v : <span style={{ color: palette.text.disabled }}>Select</span>)}
                  >
                    {(getFocusOptionsFromMapping(sportsMappings, trainingForm.sport) || []).map((s: string) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.focus && <FormHelperText>{fieldErrors.focus}</FormHelperText>}
                </FormControl>
              </Box>

              {/* Planned / Zones section */}
              {isLog ? (
                <Box>
                  <Typography
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 1.5,
                      fontSize: 11,
                      fontWeight: 600,
                      color: palette.text.secondary,
                      mb: 1,
                    }}
                  >
                    Log Training Data
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '16px 52px 1fr 100px 60px',
                      columnGap: 1,
                      alignItems: 'center',
                      px: 1,
                      mb: 1,
                    }}
                  >
                    <Box />
                    <Box />
                    <Box />
                    <Typography
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        fontSize: 10,
                        fontWeight: 600,
                        color: palette.text.secondary,
                        textAlign: 'center',
                      }}
                    >
                      Hours
                    </Typography>
                    <Typography
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        fontSize: 10,
                        fontWeight: 600,
                        color: palette.text.secondary,
                        textAlign: 'center',
                      }}
                    >
                      {measurementUnit === 'km' ? 'KM' : 'Miles'}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '16px 52px 1fr 100px 60px',
                      columnGap: 1,
                      alignItems: 'center',
                      py: 1.5,
                      px: 1,
                      backgroundColor: palette.background.subtle,
                      borderRadius: 2,
                      border: `1px solid ${palette.grey[200]}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                      mb: 1,
                    }}
                  >
                    <FiberManualRecordIcon sx={{ fontSize: 12, color: palette.text.primary }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: palette.text.primary }}>
                      Total
                    </Typography>
                    <Box />
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ textAlign: 'center', color: palette.text.primary }}
                    >
                      {formatDuration(totalDuration)} hr
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ textAlign: 'center', color: palette.text.primary }}
                    >
                      {totalDistance.toFixed(1)} {measurementUnit}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  {WORKOUT_LOG_ZONE_DISPLAY_CONFIG.map(({ label, color, zoneIdx }) => {
                    const barFill =
                      totalDuration > 0 ? (trainingForm.durationComplete[zoneIdx] / totalDuration) * 100 : 0;

                    return (
                      <Box
                        key={label}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '16px 52px 1fr 100px 60px',
                          columnGap: 1,
                          alignItems: 'center',
                          py: 1.25,
                          px: 1,
                          borderRadius: 1.5,
                          transition: 'background-color 0.12s ease',
                          '&:hover': { backgroundColor: 'rgba(13, 97, 255, 0.04)' },
                        }}
                      >
                        <FiberManualRecordIcon sx={{ fontSize: 12, color }} />
                        <Typography variant="subtitle2" sx={{ color: palette.text.primary }}>
                          {label}
                        </Typography>
                        <Box
                          sx={{
                            overflow: 'hidden',
                            borderRadius: '3px',
                            height: 6,
                            backgroundColor: palette.grey[200],
                          }}
                        >
                          <Box
                            sx={{
                              width: `${barFill}%`,
                              height: '100%',
                              backgroundColor: color,
                              borderRadius: '3px',
                              transition: 'width 0.2s ease',
                            }}
                          />
                        </Box>
                        <Box sx={{ '& .MuiTextField-root': { width: '100%' } }}>
                          <COETrainingDurationInput
                            value={trainingForm.durationComplete[zoneIdx]}
                            onChange={(seconds) => handleDurationChange(seconds, zoneIdx)}
                          />
                        </Box>
                        <TextField
                          value={trainingForm.distanceComplete[zoneIdx] || '0.0'}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDistanceChange(e, zoneIdx)}
                          size="small"
                          type="number"
                          label="Distance"
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                            '& input': { textAlign: 'center', py: '8px', px: '2px' },
                          }}
                          inputProps={{ min: 0, step: 0.1 }}
                          placeholder="0.0"
                        />
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ bgcolor: palette.grey[100], borderRadius: 2, overflow: 'hidden' }}>
                  {/* Header row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px 16px' }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ letterSpacing: 0.8, flex: 1 }}
                    >
                      PLANNED
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ letterSpacing: 0.8, mr: 3.5 }}
                    >
                      DURATION
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                      DISTANCE
                    </Typography>
                  </Box>
                  <Divider />

                  <Box
                    id="training-field-distance"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 16px',
                      gap: 1,
                      ...(fieldErrors.distance && errorRowSx),
                    }}
                  >
                    <FiberManualRecordIcon
                      sx={{ fontSize: 10, color: fieldErrors.distance ? palette.error.main : palette.primary.main }}
                    />
                    <Typography fontWeight={700} sx={{ flex: 1 }}>
                      Total
                    </Typography>
                    <COEDurationInput
                      value={trainingForm.duration as any}
                      height={32}
                      width={36}
                      fontSize={14}
                      onChange={(seconds) => setTrainingForm((v: any) => ({ ...v, duration: seconds }))}
                    />
                    <InputBase
                      value={trainingForm.distance || ''}
                      placeholder="0.0 mi"
                      onChange={(e) => {
                        setTrainingForm((v: any) => ({ ...v, distance: e.target.value }));
                        clearFieldError('distance');
                      }}
                      sx={{ width: 70, fontSize: 14, ml: 1, '& input': { textAlign: 'right' } }}
                    />
                  </Box>
                  <FieldErrorCaption message={fieldErrors.distance} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ padding: '12px 16px', borderTop: `1px solid ${palette.divider}` }}>
        <Button
          variant="contained"
          fullWidth
          onClick={createTraining}
          disabled={loading}
          sx={{ height: 48, borderRadius: 2, fontSize: 16, fontWeight: 600, textTransform: 'none' }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : isLog ? 'Log Training' : 'Create Training'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default COECreatePersonalTrainingModal;
